/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * Registers the names of the virtual channels to be used by the client and provides
 * a VirtualChannelInitEvent callback function through which Remote Desktop Services
 * notifies the client about events that affect the client connection.
 *
 * Only support one add-in at a time, thus can avoid the extra layer of map.
 *
 * @format
 */

import { Injectable } from "@angular/core";
import { WTS_INFO_CLASS } from "./rdpvcbridge.const";
import { OptionManager } from "./option-manager";
import { SessionInfoResponse } from "./vc-wire-message";
import { StringUtils } from "../../../../core/libs/string-utils";
import { Logger } from "../../../../core/libs/logger";
// TODO: reorg in later patch
import { ChannelDescriptor } from "../../../../../../SDK/src/lib/interfaces/struct-v1";
export { ChannelDescriptor } from "../../../../../../SDK/src/lib/interfaces/struct-v1";

export interface VCMessage {
   channelInfo: ChannelDescriptor;
   data: Uint8Array;
}

// Data would be split into chunk and passed by multiple calls on DataCallback
export type DataCallback = (channel: ChannelDescriptor, data: Uint8Array) => void;
export type ConnectCallback = (channel: ChannelDescriptor) => void;
export type DisconnectCallback = (channel: ChannelDescriptor) => void;

/**
 * The ChannelCallbacks should be passed to this service only once, and the
 * dispatch to add-ins should be maintained in that level at client side.
 * For this version, only one add-in is supported.
 */
export interface ChannelCallbacks {
   onReceiveData: DataCallback;
   onVchanConnected: ConnectCallback;
   onVchanDisconnected: DisconnectCallback;
}

interface PayloadData {
   channelName: string;
   nBytes: number;
   pData: Array<number>;
}

@Injectable()
export abstract class VcClient {
   static readonly rdpvcbridgeVersion = "3.2.1";
   protected optionString: string;
   protected optionManager;
   private inited;
   protected logger = new Logger(Logger.RDPVCBRIDGE, null, Logger.Modules.RDPVCBridge);
   private callbacks: ChannelCallbacks = null;
   protected uuid: string;
   constructor() {}
   /**
    * Kill switch OFF, not from SDK, no valid options would all block the
    * vcClient from being inited
    */
   public init = (uuid, channelOptions: string, callbacks: ChannelCallbacks) => {
      // this.optionManagerMap.set(featureName, new OptionManager());
      this.optionManager = this.createOptionManager();
      if (!channelOptions) {
         this.logger.info("no channel option, disable vcClient");
         this.reset();
         return;
      }
      this.logger.debug("generating vchan string from " + channelOptions);
      if (!this.optionManager.fromString(channelOptions)) {
         this.logger.info("invalid option detected, skip vcClient init");
         this.reset();
         return;
      }
      if (!callbacks) {
         this.logger.info("not init vcClient for missing backback");
         this.reset();
         return;
      }
      this.inited = true;
      if (this.optionString) {
         this.optionString = this.optionString + "," + this.optionManager.toString();
      } else {
         this.optionString = this.optionManager.toString();
      }
      this.callbacks = callbacks;
   };

   private createOptionManager = () => {
      return new OptionManager();
   };

   public reset = () => {
      this.logger.info("vchan option reset");
      this.inited = false;
      this.optionManager.clear();
   };

   /**
    * @returns whether vcClient had been inited
    */
   public isEnabled = (): boolean => {
      return this.inited;
   };

   public onMessage(message: any, sendResponse, sessionKey: string) {
      if (!message) {
         this.logger.error("message not exist");
         return;
      }
      const payload = message.payload;
      const header = message.header;
      if (!payload || !header) {
         this.logger.error("invalid message received" + JSON.stringify(message));
         return;
      }
      this.logger.debug("processing rdpvcbridge message of type " + payload.unionType + " for session: " + sessionKey);
      this.logger.dump("message content:" + JSON.stringify(message));
      switch (payload.unionType) {
         case "SessionInfoQuery": {
            this.handleSessionInfoQuery(header, payload, sendResponse, new SessionInfoResponse(header));
            break;
         }
         case "Data": {
            if (!!payload.data && payload.data.channelName) {
               // remove the extra \0 from the string
               payload.data.channelName = payload.data.channelName.replaceAll("\0", "");
            }
            this.handleData(header, payload.data, sessionKey);
            break;
         }
         default:
      }
   }

   // message are arrived in order
   private handleData = (header, payloadData: PayloadData, sessionKey: string) => {
      if (!payloadData) {
         this.logger.error("no data for stream message");
         return;
      }
      if (payloadData.nBytes <= 0) {
         this.logger.warning("no message content for stream message");
         return;
      }
      const payloadUint8 = Uint8Array.from(payloadData.pData);
      if (!!this.callbacks && typeof this.callbacks.onReceiveData === "function") {
         this.callbacks.onReceiveData(
            {
               sessionKey: sessionKey,
               channelName: payloadData.channelName,
               uuid: this.uuid
            },
            payloadUint8
         );
      } else {
         this.logger.trace("no onReceiveData defined");
      }
   };

   private handleSessionInfoQuery = (header, payload, sendResponse, response: SessionInfoResponse) => {
      const payloadData = payload.sessionInfoQuery;
      const classInfo = <WTS_INFO_CLASS>payloadData.infoClass;
      const castConfig = {
         payload: "SessionInfoResponse"
      };
      this.logger.debug("process query for " + classInfo);
      switch (classInfo) {
         case WTS_INFO_CLASS.WTSGetRemoteSDKVersion: {
            // pretend to be a old client, change when needed
            let AgentSDKVersion;
            if (payloadData.nBytes) {
               AgentSDKVersion = payloadData.pData;
            }
            this.logger.dump("version data:" + AgentSDKVersion);
            // only remove one /0 for spotting potential issues
            this.logger.info(
               "Agent SDK version received: " +
                  Array.from(AgentSDKVersion)
                     .map((n) => {
                        return String.fromCharCode(<number>n);
                     })
                     .join("")
                     .replace("\0", "")
            );
            const clientSDKversion = VcClient.rdpvcbridgeVersion;
            this.logger.info("return version: " + clientSDKversion);

            const version = StringUtils.stringToUint8Array(clientSDKversion, true);
            sendResponse(response.generateResponse(version), castConfig);
            break;
         }
         case WTS_INFO_CLASS.WTSIsNestedSession: {
            // It's impossible for nested mode on Chrome Client, since Agent doesn't support Chrome OS, year 2021
            this.logger.debug("not support nested mode");
            const notSupportNested = StringUtils.stringToUint8Array("0", true);
            sendResponse(response.generateResponse(notSupportNested), castConfig);
            break;
         }
         case WTS_INFO_CLASS.WTSChannelOptions: {
            this.logger.info("response with channel options of: " + this.optionString);

            if (this.optionString && this.optionString.length > 0) {
               const options = StringUtils.stringToUint8Array(this.optionString, true);
               sendResponse(response.generateResponse(options), castConfig);
            } else {
               sendResponse(response.generateResponse([]), castConfig);
            }
            break;
         }
         /*
          * Before IsDynamicChannelRegistered_V1 the client did not implement
          * the message so we have to assume that it is registered.
          * Before IsDynamicChannelRegistered_V2 the client had a bug where it
          * would always return TRUE so we have to assume that it is registered.
          *
          * For now, always return not support to disable dynamic channel
          */
         case WTS_INFO_CLASS.WTSIsDynamicChannelRegistered: {
            // return not support for Dynamic channel
            this.logger.info("get dynamic channel register event, return not support");
            sendResponse(response.generateResponse([]), castConfig);
            break;
         }
         case WTS_INFO_CLASS.WTSClientAddress:
         case WTS_INFO_CLASS.WTSClientName:
         case WTS_INFO_CLASS.WTSUserName:
         case WTS_INFO_CLASS.WTSDomainName: {
            Logger.error("unexpected query on classInfo for client info: " + classInfo);
            break;
         }
         default:
            Logger.error("unexpected classInfo:" + classInfo);
      }
   };
}
